import React, { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * Icon Registry System
 * Centralized management of all icons across the application
 * Supports SVG, image, and React component icons with fallback logic
 */

interface IconConfig {
  type: 'svg' | 'image' | 'component';
  src?: string;                    // For images
  svg?: string;                    // For SVG code
  component?: React.FC<any>;       // For React components
  fallback?: IconConfig;           // Fallback if primary fails
  sizes?: {
    sm: string;
    md: string;
    lg: string;
  };
}

// SVG definitions for common icons

const INBOX_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" rx="22" fill="#2563EB" /><g transform="translate(16, 16) scale(2.8)" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V9c0-1.1.9-2 2-2z" stroke="#6EE7B7" opacity="0.4" /><path d="M4 5h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V7c0-1.1.9-2 2-2z" stroke="white" /><polyline points="22,7 12,14 2,7" stroke="white" /><path d="M2 14v3a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3" stroke="white" opacity="0.6"/></g></svg>`;

const DISCOVER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" rx="22" fill="#5B21B6" /><g transform="translate(16, 16) scale(2.8)" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" stroke="white" /><line x1="12" y1="2" x2="12" y2="4" stroke="white" opacity="0.5" /><line x1="12" y1="20" x2="12" y2="22" stroke="white" opacity="0.5" /><line x1="20" y1="12" x2="22" y2="12" stroke="white" opacity="0.5" /><line x1="2" y1="12" x2="4" y2="12" stroke="white" opacity="0.5" /><g transform="rotate(45, 12, 12)"><path d="M12 4l3 8-3 1-3-1z" fill="#FDE047" stroke="#FDE047" /><path d="M12 20l-3-8 3-1 3 8z" fill="white" stroke="white" opacity="0.8" /><circle cx="12" cy="12" r="1" fill="#5B21B6" stroke="none" /></g></g></svg>`;

const HOME_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" rx="22" fill="#1E293B" /><g transform="translate(16, 16) scale(2.8)" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="white" /><polyline points="9 22 9 12 15 12 15 22" stroke="white" /></g></svg>`;

const SETTINGS_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" rx="22" fill="#334155" /><g transform="translate(16, 16) scale(2.8)" fill="none" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3" stroke="white" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="#94A3B8" /></g></svg>`;

/**
 * Central icon registry
 * Maps icon names to their configurations
 */
const ICON_REGISTRY: Record<string, IconConfig> = {
  home: {
    type: 'svg',
    svg: HOME_SVG,
    sizes: { sm: 'h-5 w-5', md: 'h-6 w-6', lg: 'h-7 w-7' }
  },
  inbox: {
    type: 'svg',
    svg: INBOX_SVG,
    sizes: { sm: 'h-5 w-5', md: 'h-6 w-6', lg: 'h-7 w-7' }
  },
  discover: {
    type: 'svg',
    svg: DISCOVER_SVG,
    sizes: { sm: 'h-5 w-5', md: 'h-6 w-6', lg: 'h-7 w-7' }
  },
  settings: {
    type: 'svg',
    svg: SETTINGS_SVG,
    sizes: { sm: 'h-5 w-5', md: 'h-6 w-6', lg: 'h-7 w-7' }
  },
};

interface IconProps extends ComponentProps<'div'> {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  fallbackText?: string;
}

/**
 * Icon Component
 * Renders icons from the registry with fallback support
 * 
 * @param name - Icon name from registry
 * @param size - Icon size (sm, md, lg)
 * @param className - Additional CSS classes
 * @param fallbackText - Text to show if icon fails to load
 */
export const Icon: React.FC<IconProps> = ({ 
  name, 
  size = 'md', 
  className,
  fallbackText,
  ...props 
}) => {
  const config = ICON_REGISTRY[name];
  
  if (!config) {
    console.warn(`Icon "${name}" not found in registry`);
    return <span className={cn('inline-flex items-center justify-center', className)}>{fallbackText || '?'}</span>;
  }

  const sizeClass = config.sizes?.[size] || 'h-6 w-6';
  const combinedClassName = cn(sizeClass, className);

  // Render image icon
  if (config.type === 'image' && config.src) {
    return (
      <img
        src={config.src}
        alt={name}
        className={combinedClassName}
        onError={(e) => {
          // Fallback to SVG if image fails
          if (config.fallback?.svg) {
            const svg = document.createElement('svg');
            svg.innerHTML = config.fallback.svg;
            svg.className = combinedClassName;
            e.currentTarget.replaceWith(svg);
          }
        }}
        {...props}
      />
    );
  }

  // Render SVG icon
  if (config.type === 'svg' && config.svg) {
    return (
      <span
        className={cn('inline-flex items-center justify-center [&_svg]:h-full [&_svg]:w-full', combinedClassName)}
        dangerouslySetInnerHTML={{ __html: config.svg }}
        {...props}
      />
    );
  }

  // Render React component icon
  if (config.type === 'component' && config.component) {
    const Component = config.component;
    return <Component className={combinedClassName} {...props} />;
  }

  return null;
};


